import { createApp } from 'vue'

// Close a dropdown when clicking outside of it.
// Thanks to https://stackoverflow.com/questions/36170425/detect-click-outside-element
// Vue 3 renamed the directive hooks (bind -> beforeMount, unbind -> unmounted)
// and dropped binding.expression, so the handler is passed via binding.value.
const clickOutside = {
    beforeMount(el, binding) {
        el.clickOutsideEvent = function (event) {
            if (!(el.contains(event.target))) {
                binding.value(event);
            }
        };
        document.addEventListener('click', el.clickOutsideEvent);
    },
    unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
    },
};

let userBarApp = null;

const unmountUserBar = () => {
    if (userBarApp) {
        userBarApp.unmount();
        userBarApp = null;
    }
};

document.addEventListener('turbo:before-render', unmountUserBar);

document.addEventListener('turbo:load', () => {
    const userBar = document.getElementById('user-bar-view');
    if (userBar == null) return;
    unmountUserBar();

    // Seed from the server-rendered payload so the bar paints the real
    // numbers immediately instead of starting at zero and popping.
    let seed = {};
    try { seed = JSON.parse(userBar.dataset.initial || '{}'); } catch (e) { seed = {}; }

    userBarApp = createApp({
        data() {
            return {
                messages: 0,
                friend_requests: 0,
                bux: 0,
                daily_bux_streak: 0,
                daily_bux_streak_cap: 5,
                island_club: false,
                daily_bux_eligibility: "",
                forum_week_streak: 0,
                forum_days_this_week: 0,
                forum_days_needed: 2,
                forum_next_reward: 0,
                bux_open: false,
                notification_count: 0,
                notification_groups: [],
                notifications_open: false,
                timer: null,
                // Spread last: these are the real values and must win over the
                // zero defaults above.
                ...seed,
                // The payload names this unread_notifications; the template
                // binds notification_count.
                notification_count: seed.unread_notifications || 0,
            }
        },
        created() {
            this.update();
            this.timer = setInterval(this.update, 5000);
        },
        methods: {
            update() {
                axios.get('/users/status.json').then((res) => {
                    const status = res.data;
                    this.messages = status.messages;
                    this.friend_requests = status.friend_requests;
                    this.bux = status.bux;
                    this.daily_bux_streak = status.daily_bux_streak;
                    this.daily_bux_streak_cap = status.daily_bux_streak_cap;
                    this.island_club = status.island_club;
                    this.daily_bux_eligibility = status.daily_bux_eligibility;
                    this.forum_week_streak = status.forum_week_streak;
                    this.forum_days_this_week = status.forum_days_this_week;
                    this.forum_days_needed = status.forum_days_needed;
                    this.forum_next_reward = status.forum_next_reward;
                    this.notification_groups = status.notification_groups;
                    this.notification_count = status.unread_notifications;
                });
            },
            toggle_notifications() {
                this.notifications_open = !this.notifications_open;
                if (this.notifications_open && this.notification_count > 0) {
                    axios.post('/account/notifications_read.json');
                    this.notification_count = 0;
                }
            },
            toggle_bux() {
                this.bux_open = !this.bux_open;
            },
            close_notifications() {
                this.notifications_open = false;
            },
            close_bux() {
                this.bux_open = false;
            },
        },
        beforeUnmount() {
            clearInterval(this.timer);
        }
    });
    userBarApp.directive('click-outside', clickOutside);
    userBarApp.mount(userBar);
});

// ---- Live market-tax preview on bux inputs (trade + market forms) ----------
// Inputs carry data-tax-rate (receiver's rate), data-tax-member, data-tax-receiver
// ('you' or a username) and data-tax-preview (selector of the output element).
const updateTaxPreview = (el) => {
    const out = document.querySelector(el.dataset.taxPreview);
    if (!out) return;
    const v = parseInt(el.value, 10) || 0;
    if (v <= 0) { out.textContent = ''; return; }
    const rate = parseFloat(el.dataset.taxRate);
    const member = el.dataset.taxMember === 'true';
    const who = el.dataset.taxReceiver;
    const tax = Math.round(v * rate);
    const net = v - tax;
    const savings = Math.round(v * 0.15) - Math.round(v * 0.10);
    // Match the server-side bux_format helper: $ + locale-grouped digits.
    const money = (n) => '$' + n.toLocaleString();
    let text;
    if (who === 'you') {
        text = `You'll receive ${money(net)} after ${money(tax)} tax. `;
        text += member ? `Island Club is saving you ${money(savings)}.`
                       : `Island Club would save you ${money(savings)}.`;
    } else {
        text = `${who} will receive ${money(net)} after ${money(tax)} tax. `;
        text += member ? `Island Club saves them ${money(savings)}.`
                       : `Island Club would save them ${money(savings)}.`;
    }
    out.textContent = text;
};
document.addEventListener('input', (e) => {
    if (e.target.matches && e.target.matches('.js-tax-input')) updateTaxPreview(e.target);
});
document.addEventListener('turbo:load', () => {
    document.querySelectorAll('.js-tax-input').forEach(updateTaxPreview);
});

// ---- Admin niceties: live colour swatches + conditional suspend-days -------
document.addEventListener('input', (e) => {
    if (e.target.matches && e.target.matches('.js-colour-input')) {
        const swatch = e.target.parentElement.querySelector('.colour-swatch');
        if (swatch) swatch.style.background = e.target.value;
    }
});
const syncConditionalFields = () => {
    // Suspension duration only matters while a suspend action is chosen
    // ('suspend' on the report form, 'suspension' on the direct form).
    document.querySelectorAll('.js-account-action').forEach((sel) => {
        const days = sel.closest('form')?.querySelector('.js-suspend-days');
        if (days) days.hidden = !['suspend', 'suspension'].includes(sel.value);
    });
    // The replacement-text box only matters while "Replace" is chosen.
    document.querySelectorAll('.js-content-action').forEach((sel) => {
        const edit = sel.closest('form')?.querySelector('.js-content-edit');
        if (edit) edit.hidden = sel.value !== 'edit';
    });
};
document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('.js-account-action, .js-content-action')) syncConditionalFields();
});
document.addEventListener('turbo:load', syncConditionalFields);
// Profiles remount a Vue app over the page after turbo:load; re-sync after it settles.
document.addEventListener('turbo:load', () => setTimeout(syncConditionalFields, 50));


// ---- Sticky status-bar height ----------------------------------------------
// The mobile nav popovers (bux, notifications) drop from just below the status
// bar. Publish its measured height so the CSS never has to guess.
function syncUserBarHeight() {
    const bar = document.getElementById('user-bar');
    if (!bar) return;
    document.documentElement.style.setProperty('--gi-userbar-h', bar.offsetHeight + 'px');
}
document.addEventListener('turbo:load', syncUserBarHeight);
document.addEventListener('DOMContentLoaded', syncUserBarHeight);
window.addEventListener('resize', syncUserBarHeight);
window.addEventListener('orientationchange', syncUserBarHeight);

// ---- Item rejection reason --------------------------------------------------
// "Something else" reveals the free-text box next to the reason dropdown.
document.addEventListener('change', (e) => {
    const select = e.target.closest && e.target.closest('.js-reject-reason');
    if (!select) return;
    const other = select.parentElement.querySelector('.js-reject-other');
    if (!other) return;
    const needsText = select.value === 'Something else';
    other.hidden = !needsText;
    if (needsText) other.focus();
});

// ---- Inbox bulk bar ---------------------------------------------------------
// Reveal bulk actions only when conversations are actually selected.
function syncBulkBar() {
    document.querySelectorAll('.js-bulk-form').forEach((form) => {
        const boxes = form.querySelectorAll('input[type=checkbox][name="thread_ids[]"]');
        const any = [...boxes].some((b) => b.checked);
        form.classList.toggle('has-selection', any);
    });
}
document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('.js-bulk-form input[type=checkbox]')) syncBulkBar();
});
document.addEventListener('turbo:load', syncBulkBar);
